import { Box, Checkbox, Chip, Typography } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";

import { PaymentLinkExpiryIcon, PaymentMemberShipIcon } from "../../../../../assets/icons/MUI/PaymentIcons";

const GOALNGO_FEES = 0.1;

const ReviewPayment = () => {
  const { t } = useTranslation();
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext();

  const { amount, currency, expiryDate, paymentCategory, paymentFor, recipientsOutSideGoalngo } = watch();

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", rowGap: "24px" }}>
      <Box sx={{ borderRadius: "16px", border: "1px solid #EAECF0", overflow: "hidden", marginBottom: "16px" }}>
        <Typography
          component="h5"
          sx={{
            color: "#FFF",
            fontFamily: "Montserrat",
            fontSize: "18px",
            fontStyle: "normal",
            fontWeight: 600,
            lineHeight: "28px",
            background: "#106048",
            padding: "12px 24px",
          }}
        >
          {t("pages.payment.createPaymentLink.reviewPayment")}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            rowGap: "24px",
            padding: "24px",
            borderBottom: "1px solid #EAECF0",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", rowGap: "8px" }}>
            <Typography
              component="h6"
              sx={{
                color: "#111",
                fontFamily: "Montserrat",
                fontSize: "18px",
                fontStyle: "normal",
                fontWeight: 600,
                lineHeight: "28px",
              }}
            >
              {t("pages.payment.createPaymentLink.paymentForDescription")}
            </Typography>
            <Typography
              component="span"
              sx={{
                color: "#475467",
                fontFamily: "Montserrat",
                fontSize: "16px",
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "24px",
              }}
            >
              {paymentFor}
            </Typography>
            <Box sx={{ display: "flex", columnGap: "12px", alignItems: "center" }}>
              <PaymentMemberShipIcon />
              <Typography
                component="span"
                sx={{
                  color: "#111",
                  fontFamily: "Montserrat",
                  fontSize: "16px",
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: "24px",
                }}
              >
                {t(`pages.payment.createPaymentLink.${paymentCategory}`)}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", columnGap: "12px", alignItems: "center" }}>
              <PaymentLinkExpiryIcon />
              <Typography
                component="span"
                sx={{
                  color: "#111",
                  fontFamily: "Montserrat",
                  fontSize: "16px",
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: "24px",
                }}
              >
                {t("pages.payment.createPaymentLink.linkExpiry")}
                <Typography component="strong" sx={{ fontWeight: 600 }}>
                  {format(expiryDate, "dd/MM/yyyy")}
                </Typography>
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", rowGap: "8px" }}>
            <Typography
              component="h6"
              sx={{
                color: "#111",
                fontFamily: "Montserrat",
                fontSize: "18px",
                fontStyle: "normal",
                fontWeight: 600,
                lineHeight: "28px",
              }}
            >
              {t("pages.payment.createPaymentLink.reviewRecipient")}
            </Typography>
            <Typography
              component="span"
              sx={{
                color: "#475467",
                fontFamily: "Montserrat",
                fontSize: "16px",
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "24px",
              }}
            >
              {t("pages.payment.createPaymentLink.invitedOutsideGoalngo")}
            </Typography>
            <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {recipientsOutSideGoalngo.map(
                (email: string, idx: number) => idx < 5 && <Chip key={idx} label={email} variant="outlined" />
              )}
              {recipientsOutSideGoalngo.length > 5 && (
                <Chip key={5} label={`+${recipientsOutSideGoalngo.length - 5}`} variant="filled" />
              )}
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            rowGap: "24px",
            padding: "24px",
            borderBottom: "1px solid #EAECF0",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Typography
              component="span"
              sx={{
                color: "#111",
                fontFamily: "Montserrat",
                fontSize: "18px",
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "28px",
              }}
            >
              {t("pages.payment.createPaymentLink.paymentAmount")}
            </Typography>
            <Typography
              component="strong"
              sx={{
                color: "#111",
                fontFamily: "Montserrat",
                fontSize: "18px",
                fontStyle: "normal",
                fontWeight: 600,
                lineHeight: "28px",
              }}
            >
              {Number(amount).toFixed(2)}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Typography
              component="span"
              sx={{
                color: "#111",
                fontFamily: "Montserrat",
                fontSize: "18px",
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "28px",
              }}
            >
              {t("pages.payment.createPaymentLink.goalngoFees")}
            </Typography>
            <Typography
              component="strong"
              sx={{
                color: "#111",
                fontFamily: "Montserrat",
                fontSize: "18px",
                fontStyle: "normal",
                fontWeight: 600,
                lineHeight: "28px",
              }}
            >
              {(Number(amount) * GOALNGO_FEES).toFixed(2)}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            padding: "24px",
          }}
        >
          <Typography
            component="strong"
            sx={{
              color: "#111",
              fontFamily: "Montserrat",
              fontSize: "18px",
              fontStyle: "normal",
              fontWeight: 600,
              lineHeight: "28px",
            }}
          >
            {t("pages.payment.createPaymentLink.totalReceived")}
          </Typography>
          <Typography
            component="strong"
            sx={{
              color: "#111",
              fontFamily: "Montserrat",
              fontSize: "18px",
              fontStyle: "normal",
              fontWeight: 600,
              lineHeight: "28px",
            }}
          >
            {`${(Number(amount) - Number(amount) * GOALNGO_FEES).toFixed(2)} ${currency}`}
          </Typography>
        </Box>
      </Box>
      <Controller
        name="acceptTermsAndConditions"
        control={control}
        rules={{ required: "ACCEPT_TERMS_AND_CONDITIONS" }}
        render={({ field }) => (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Checkbox {...field} checked={field.value} size="small" />
              <Typography
                sx={{
                  color: "#111",
                  fontFamily: "Montserrat",
                  fontSize: "14px",
                  fontStyle: "normal",
                  fontWeight: 500,
                  lineHeight: "20px",
                }}
              >
                {t("pages.payment.createPaymentLink.acceptGoalngo")}
                <Typography
                  sx={{
                    color: "#106048",
                    textDecoration: "underline",
                    cursor: "pointer",
                    display: "inline-block",
                    margin: "0 3px",
                  }}
                >
                  {t("pages.payment.createPaymentLink.termsAndConditions")}
                </Typography>
                {t("pages.payment.createPaymentLink.forPayment")}
              </Typography>
            </Box>
            {errors?.acceptTermsAndConditions?.message && (
              <Typography
                sx={{
                  color: "red",
                  fontSize: "12px",
                  fontWeight: 500,
                  marginTop: "4px",
                }}
              >
                {t(`pages.payment.createPaymentLink.${String(errors?.acceptTermsAndConditions.message)}`)}
              </Typography>
            )}
          </Box>
        )}
      />
    </Box>
  );
};

export default ReviewPayment;
